
import { api } from '../lib/api-client.js';
import { useAuthStore, type AuthKind } from '../stores/auth-store.js';

import type { CurrentUser } from '@sitewise/shared';

export interface SessionResponse {
  accessToken: string;
  accessTokenExpiresAt: string;
  refreshToken: string;
  refreshTokenExpiresAt: string;
  user: CurrentUser;
}

export interface AdminTotpSetupResponse {
  totpSetupRequired: true;
  totpSecret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

export interface InvitationPreview {
  email: string;
  name: string;
  role: string;
  orgName: string;
  orgSlug: string;
}

function persistSession(kind: AuthKind, response: SessionResponse): SessionResponse {
  useAuthStore.getState().setTokens({
    kind,
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
  });
  return response;
}

export async function login(input: {
  email: string;
  password: string;
  totpCode?: string;
}): Promise<SessionResponse> {
  const response = await api<SessionResponse>('/auth/login', {
    method: 'POST',
    body: input,
    skipRefresh: true,
  });
  return persistSession('org', response);
}

export async function logout(): Promise<void> {
  const { refreshToken, kind, clear } = useAuthStore.getState();
  if (refreshToken) {
    const path = kind === 'admin' ? '/admin/auth/logout' : '/auth/logout';
    try {
      await api(path, { method: 'POST', body: { refreshToken }, skipRefresh: true });
    } catch {
      // tolerate logout failures — we still clear the local session
    }
  }
  // Phase 10 — clear queued offline mutations + drafts + photo blobs so
  // the next user on a shared device can't see the previous tenant's
  // private data. We swallow Dexie errors because logout must succeed
  // even if IndexedDB is unavailable (private browsing, etc).
  try {
    const { default: Dexie } = await import('dexie');
    await Dexie.delete('sitewise-offline');
  } catch {
    /* noop */
  }
  clear();
}

export async function forgotPassword(email: string): Promise<void> {
  await api('/auth/forgot-password', {
    method: 'POST',
    body: { email },
    skipRefresh: true,
  });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api('/auth/reset-password', {
    method: 'POST',
    body: { token, newPassword },
    skipRefresh: true,
  });
}

export async function inspectInvitation(token: string): Promise<InvitationPreview> {
  return api<InvitationPreview>('/auth/accept-invitation/inspect', {
    method: 'POST',
    body: { token },
    skipRefresh: true,
  });
}

export async function acceptInvitation(input: {
  token: string;
  password: string;
}): Promise<SessionResponse> {
  const response = await api<SessionResponse>('/auth/accept-invitation', {
    method: 'POST',
    body: input,
    skipRefresh: true,
  });
  return persistSession('org', response);
}

// Vendor sign-in (request / consume) moved to `api/vendor-auth.ts` in
// Phase 3 — keeping a single canonical implementation prevents drift
// between the staff and vendor session models.

export async function adminLogin(input: {
  email: string;
  password: string;
  totpCode?: string;
}): Promise<SessionResponse | AdminTotpSetupResponse> {
  const response = await api<SessionResponse | AdminTotpSetupResponse>(
    '/admin/auth/login',
    { method: 'POST', body: input, skipRefresh: true },
  );
  if ('totpSetupRequired' in response) return response;
  return persistSession('admin', response);
}

export async function adminTotpConfirm(input: {
  email: string;
  password: string;
  totpCode: string;
  totpSecret: string;
}): Promise<SessionResponse> {
  const response = await api<SessionResponse>('/admin/auth/totp/confirm', {
    method: 'POST',
    body: input,
    skipRefresh: true,
  });
  return persistSession('admin', response);
}

export async function exchangeHandoff(handoffToken: string): Promise<{
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
  refreshTokenExpiresAt: string;
}> {
  const response = await api<{
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresAt: string;
    refreshTokenExpiresAt: string;
  }>('/handoff/exchange', {
    method: 'POST',
    body: { handoffToken },
    skipRefresh: true,
    surfaceOverride: 'org',
  });
  useAuthStore.getState().setTokens({
    kind: 'org',
    accessToken: response.accessToken,
    refreshToken: response.refreshToken,
  });
  return response;
}

export async function fetchMe(): Promise<{ user: CurrentUser }> {
  return api<{ user: CurrentUser }>('/me');
}

export interface PublicBranding {
  slug: string;
  name: string;
  logoUrl: string | null;
  primaryColorHex: string | null;
}

export async function fetchPublicBranding(): Promise<PublicBranding> {
  return api<PublicBranding>('/org/public/branding', { skipRefresh: true });
}
